import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axiosClient from '../../api/axiosClient';
import { Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ClientDashboardAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axiosClient.get('/trials/me/analytics');
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch client analytics', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-6 mb-8 animate-pulse">
        <div className="h-64 bg-white rounded-2xl border border-slate-200"></div>
        <div className="h-64 bg-white rounded-2xl border border-slate-200"></div>
      </div>
    );
  }

  if (!data) return null;

  // Calculate circumference for progress ring
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  // Assume a 14 day trial for percentage math if active, otherwise 0
  const maxDays = 14; 
  const daysLeft = data.daysLeft;
  const progressOffset = circumference - (Math.min(daysLeft, maxDays) / maxDays) * circumference;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="grid md:grid-cols-2 gap-6 mb-8"
    >
      {/* Trial Status Ring */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
        <h3 className="text-lg font-bold text-slate-800 mb-6 w-full text-left">Sandbox Status</h3>
        
        <div className="relative flex items-center justify-center mb-4">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              className="text-slate-100"
              strokeWidth="8"
              stroke="currentColor"
              fill="transparent"
              r={radius}
              cx="64"
              cy="64"
            />
            <circle
              className={daysLeft > 3 ? "text-primary-500 transition-all duration-1000 ease-out" : "text-red-500 transition-all duration-1000 ease-out"}
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={progressOffset}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r={radius}
              cx="64"
              cy="64"
            />
          </svg>
          <div className="absolute flex flex-col items-center justify-center text-center">
            <span className="text-3xl font-display font-bold text-slate-900">{daysLeft}</span>
            <span className="text-[10px] uppercase font-bold text-slate-400">Days Left</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-sm font-medium">
          {data.trialStatus === 'active' ? (
            <span className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              <CheckCircle2 size={14} /> Active
            </span>
          ) : data.trialStatus === 'terminated' ? (
            <span className="flex items-center gap-1.5 text-red-600 bg-red-50 px-3 py-1 rounded-full">
              <AlertTriangle size={14} /> Terminated
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              <Clock size={14} /> {data.trialStatus || 'Pending'}
            </span>
          )}
        </div>
      </div>

      {/* Payments Bar Chart */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <h3 className="text-lg font-bold text-slate-800 mb-6">Payment History</h3>
        
        {data.paymentData && data.paymentData.length > 0 ? (
          <div className="h-[180px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.paymentData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(val) => `$${val}`} />
                <Tooltip 
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="amount" fill="#4f46e5" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[180px] w-full flex items-center justify-center text-slate-400 text-sm">
            No payments recorded yet.
          </div>
        )}
      </div>
    </motion.div>
  );
}
